import { useAuth } from '@/hooks/useAuth';
import { Spinner } from '@/components/Spinner';
import { Navigate, useLocation } from 'react-router-dom'
import { ReactNode } from 'react';

interface ProtectedRouteProps {
    children: ReactNode;
}

/**
 * Wraps a route's element. When the user is not authenticated, we
 * redirect to `/login` with `replace: true` so the protected URL does
 * NOT sit in the history stack behind the login page. That way:
 *
 *   1. After successful sign-in, the back button from the protected
 *      page goes to the originating tab (e.g. `/`), not back to the
 *      protected URL which would loop the guard again.
 *   2. The `/login` page's own back button lands on `/`, not on a
 *      half-broken protected URL that would immediately redirect
 *      again.
 */
const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
    const { user, loading } = useAuth();
    const location = useLocation();

    console.debug('[ProtectedRoute]', { hasUser: !!user, loading });

    if (loading) {
        return <Spinner />;
    }

    if (!user) {
        return (
            <Navigate
                to="/login"
                replace
                state={{ from: location.pathname + location.search }}
            />
        );
    }

    return <>{children}</>;
};


export default ProtectedRoute;